import {ColumnCounter} from "./ColumnCounter";
import {ReactElement} from 'react';

export interface TableRow<ValueType> {
    accentColor: string;
    children: TableRow<ValueType>[];
    label: string | ReactElement;
    labelClassName?: string;
    secondaryLabel?: string | ReactElement;
    labelSuffix?: string;
    subtitle?: string;
    uniqueIdentifier: string;
    values: TableCell<ValueType>[];
    isDraggable?: boolean;
}

export interface TableHeader<ValueType> {
    label: string;
    subtitle?: string;
    values: TableCell<ValueType>[];
}

export interface TableCell<ValueType> {
    originalValue: ValueType;
    originalSecondaryValue?: ValueType;
    renderedValue: string | ReactElement;
    renderedSecondaryValue?: string | ReactElement,
    isBlank: boolean;
    className?: string;
}

interface CreateTableCellParams<ValueType> {
    value: ValueType;
    secondaryValue?: ValueType;
    className?: string;
}

export abstract class TableDisplay<ValueType, TR extends TableRow<ValueType> = TableRow<ValueType>> {
    protected constructor(private readonly columnCounter: ColumnCounter,
                          private readonly renderValueFn: (value: ValueType) => string | ReactElement = (value => String(value)),
                          private readonly renderSecondaryValueFn: (value: ValueType) => string | ReactElement = (value => String(value))
    ) {
    }

    get header(): TableHeader<ValueType> {
        return {
            label: this.headerLabel,
            values: this.headerValues
        }
    }

    abstract get rows(): TR[]

    protected abstract get headerLabel(): string

    protected abstract get headerValues(): TableCell<ValueType>[]

    protected get columnCount(): number {
        return this.columnCounter.count;
    }

    protected createTableCell({value, secondaryValue, className}: CreateTableCellParams<ValueType>): TableCell<ValueType> {
        const isBlank = value === null;
        const renderValue = (v: ValueType, renderFn: (value: ValueType) => string | ReactElement) => isBlank ? '' : renderFn(v);
        return {
            originalValue: value,
            originalSecondaryValue: secondaryValue,
            renderedValue: renderValue(value, this.renderValueFn),
            renderedSecondaryValue: secondaryValue || secondaryValue === 0 ? renderValue(secondaryValue, this.renderSecondaryValueFn) : undefined,
            isBlank: isBlank,
            className: className,
        }
    }
}